'use client'

import { useState, useEffect } from 'react'
import { Menu, X } from 'lucide-react'
import { LanguageToggle } from '@/components/LanguageToggle'
import { useT } from '@/lib/useT'
import { ERP_LOGIN_URL } from '@/lib/erp'

export default function Navbar() {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeHash, setActiveHash] = useState('')

  const navLinks = [
    { label: t('nav.platform'), href: '#platform', key: 'nav.platform' },
    { label: t('nav.pricing'), href: '#pricing', key: 'nav.pricing' },
    { label: t('nav.testimonials'), href: '#testimonials', key: 'nav.testimonials' },
    { label: t('nav.faq'), href: '#faq', key: 'nav.faq' },
  ]

  useEffect(() => {
    let ticking = false
    let lastScrollY = 0

    const updateScroll = () => {
      setScrolled(lastScrollY > 50)
      ticking = false
    }

    const onScroll = () => {
      lastScrollY = window.scrollY
      if (!ticking) {
        requestAnimationFrame(updateScroll)
        ticking = true
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const ids = navLinks.map((l) => l.href.replace('#', ''))
    const observers: IntersectionObserver[] = []
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveHash(`#${id}`) },
        { rootMargin: '-30% 0px -60% 0px' }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [navLinks])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <>
      <header
        // Always solid: careers/legal pages open on a dark band, where transparent
        // would hide the Nature-green links and logo lettering.
        className={`fixed inset-x-0 top-0 z-50 bg-white/95 backdrop-blur-md transition-shadow duration-[200ms] ease-out border-b border-border-subtle ${
          scrolled || open ? 'shadow-sm' : ''
        }`}
        style={{ height: '64px' }}
      >
        <div className="mx-auto flex h-full max-w-[1200px] items-center justify-between gap-6 px-6">
          {/* Full lockup (guidelines p8) - never the symbol with typed text */}
          <a href="/" className="shrink-0 rounded-sm" aria-label="SIDR home">
            <img src="/brand/sidr-logo.svg" alt="SIDR" className="h-10 w-auto" />
          </a>

          {/* Desktop nav: small uppercase links separated by slashes (guidelines p42) */}
          <nav className="hidden md:flex items-center" aria-label="Primary navigation">
            {navLinks.map((link, i) => {
              const active = activeHash === link.href
              return (
                <span key={link.href} className="flex items-center">
                  {i > 0 && <span className="px-2 text-xs text-text-subtle" aria-hidden="true">/</span>}
                  <a
                    href={link.href}
                    aria-current={active ? 'true' : undefined}
                    className={`px-1.5 py-2 text-xs font-medium uppercase tracking-[0.12em] transition-colors duration-[140ms] ${
                      active ? 'text-nature' : 'text-text-body hover:text-nature'
                    }`}
                  >
                    {link.label}
                  </a>
                </span>
              )
            })}
          </nav>

          {/* CTA group */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageToggle />
            <a
              href={ERP_LOGIN_URL}
              className="text-xs font-medium uppercase tracking-[0.12em] text-nature hover:text-nature-hover transition-colors"
            >
              {t('nav.login')}
            </a>
            <a
              href="#signup"
              className="btn-press rounded-md bg-digital px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-nature hover:bg-digital-hover transition-colors"
            >
              {t('nav.getStarted')}
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-md text-nature hover:bg-leaf-100 transition-colors"
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="mobile-nav"
          >
            {open ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <nav
        id="mobile-nav"
        className={`fixed inset-x-0 top-[64px] z-40 md:hidden transition-all duration-[360ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
          open ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        <div className="bg-white border-b border-border-subtle p-4 space-y-1 shadow-md">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="block px-4 py-3 text-xs font-medium uppercase tracking-[0.12em] text-text-body hover:bg-leaf-50 hover:text-nature rounded-md transition-colors"
              onClick={() => setOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <div className="px-4 py-2">
            <LanguageToggle />
          </div>
          <a
            href={ERP_LOGIN_URL}
            className="block px-4 py-3 text-xs font-medium uppercase tracking-[0.12em] text-nature hover:bg-leaf-50 rounded-md transition-colors"
            onClick={() => setOpen(false)}
          >
            {t('nav.login')}
          </a>
          <a
            href="#signup"
            className="block w-full rounded-md bg-digital px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.12em] text-nature hover:bg-digital-hover transition-colors"
            onClick={() => setOpen(false)}
          >
            {t('nav.getStarted')}
          </a>
        </div>
      </nav>
    </>
  )
}
